// src/components/common/SearchTrigger.jsx
/**
 * SearchTrigger — inline search field that expands on use (mobile) / plain TextField (desktop).
 *
 * @description
 *   Replaces always-visible TextField search bars in list views. On mobile it
 *   draws a pill that ALWAYS shows the search icon AND the placeholder text,
 *   and grows to fill its row once the field is in use. The input is ALWAYS
 *   mounted in the DOM, so a tap focuses it natively and the iOS Safari
 *   keyboard opens on the FIRST tap.
 *
 *   🔴 Bis 2026-10-07 zeigte der geschlossene Zustand mobil NUR eine Lupe
 *   (`flex: '0 0 40px'`, das Eingabefeld auf `width: 0`). Der Platzhalter war
 *   damit unsichtbar: die Flaeche sagte weder WAS durchsucht wird noch DASS
 *   man hier suchen kann (Fotobefund „rma (m): Suchfeld leere Pille nur mit
 *   Lupe", `docs/backlog-auto-repair/2026-10-07-ui2-befunde-fotos.md`). Jetzt
 *   traegt der geschlossene Zustand den Platzhalter; das Feld waechst beim
 *   Fokus auf die volle Zeilenbreite (`flex: 1` + das `sx` des Aufrufers).
 *   Der `placeholder`-Text ist der einzige Traeger dieser Aussage — er gehoert
 *   deshalb in JEDEN Aufruf und muss kurz genug fuer eine 200-px-Pille sein.
 *
 * Design system: glass pill (T2) → expands to the caller's width.
 *   Höhe: `FIELD_HEIGHT_SX` (44 px unter `sm`, 40 px ab `sm`, 44 auf grobem
 *   Zeiger) — derselbe Wert, den der `FilterSheet`-Knopf daneben trägt. Die
 *   Pille stand bis 2026-10-08 auf einer festen 40; in EINER Werkzeugzeile
 *   standen damit zwei Höhen (T3-5).
 *
 * Layout rule:
 *   SearchTrigger belongs in the UTILITY ROW (row 1), NOT the filter pill row.
 *   Quick filter pills live in a separate second row below.
 *   Other utility buttons (sort, open-filter-sheet) go in the same row as search.
 *
 * Mobile behaviour:
 *   - Field shows icon + placeholder while idle; tapping it expands to the
 *     row width AND focuses it (iOS keyboard on first tap)
 *   - Blur (tap away) → collapses back; query is kept so the filter stays active
 *   - Pill outline turns primary.main when there is an active query
 *   - Clear button inside pill (×) clears the query. It is a real `IconButton`:
 *     tab stop, Enter/Space, `aria-label` — and it does NOT open the field on
 *     click (the event does not bubble to the container's `expandAndFocus`).
 *     🔴 Der FOKUS-RUECKWEG gilt NUR auf dem TASTATURWEG (R16-W-B + R15-W1,
 *     2026-10-08): traegt der Knopf den Tastaturfokus, geht der Fokus nach dem
 *     Leeren zurueck ins Feld — das oeffnet es, und das ist gewollt (sonst
 *     faellt er auf `document.body`). Auf dem ZEIGERWEG bewegt sich nichts
 *     SICHTBARES. Im OFFENEN Zustand liegt der Fokus IN ALLER REGEL im Feld —
 *     die eine Ausnahme ist der Auto-Oeffnen-Pfad (`:106-108`): ein Deep-Link
 *     mit `?q=…` klappt die Pille beim Montieren auf, OHNE den Fokus zu setzen;
 *     dort geht der Zeigerweg jetzt ohne Fokus aus (zu sehen ist auch da
 *     nichts, nur die Ausgangslage des naechsten `Tab` ist eine andere). Und
 *     EINGEKLAPPT bleibt die Pille eingeklappt — unbedingt gerufen klappte
 *     `focus()` sie dort auf. (R16-W-1 der WV 3, 2026-10-08: hier stand „offen
 *     liegt der Fokus ohnehin im Feld" ohne die Ausnahme.)
 *     🔴 Sein `aria-label` lautet „Clear search" — und auf drei Flaechen steht
 *     je ein WEITERER Knopf mit demselben Namen (`RMAInbox.jsx`,
 *     `CompetitorList.jsx`, `POInbox.jsx`), der im Leerzustand erscheint. Die
 *     Dopplung bleibt bewusst: alle leeren dieselbe Suche, also ist der gleiche
 *     Name richtig; mehrere Namen fuer eine Handlung waeren die schlechtere
 *     Auskunft. (R15-W5, 2026-10-08: die Dopplung ist aelter als dieses Bauteil
 *     und keine Folge des Tab-Stopps. R16-W-2, 2026-10-08: hier stand „auf zwei
 *     Flaechen" — gemessen sind es drei, `POInbox.jsx` war ungenannt.)
 *
 * On desktop (sm+): renders a compact standard TextField — no behaviour change.
 *
 * @useWhen
 *   - List views whose utility row carries search plus one or two controls
 *   - When search is secondary to row content (table, kanban, inbox)
 *
 * @dontUseFor
 *   - Search-as-primary-action pages — use a full-width TextField instead
 *   - Forms that need autocomplete or suggestion dropdowns
 *   - Rows without room for a readable placeholder — the caller then has to
 *     shorten `placeholder` rather than hide it (see the note above)
 *
 * @related
 *   - `FilterSheet` — the other half of the mobile toolbar (search + one
 *     filter button is the house form for a list view's utility row)
 *
 * @reuseHints
 *   - Any list whose search should be reachable by keyboard: `shortcut="/"` is
 *     the whole wiring, no caller-side listener
 *   - Server-backed lists: `onCommit` replaces a hand-rolled debounce, the
 *     caller only writes the committed term into its query state
 *
 * @props
 *   value       : string          — controlled search query (in buffer mode it
 *                                   SEEDS the buffer; the caller's value is the
 *                                   committed one)
 *   onChange    : (q) => void     — called on input change (default path only)
 *   placeholder : string          — input placeholder (default "Search…"); the
 *                                   ONLY visible label of the idle field.
 *                                   🔴 `type="search"` is deliberately NOT set:
 *                                   it would change the role for all 16 existing
 *                                   callers and add WebKit's own clear button
 *                                   next to the ✕ (which has its own contract).
 *   label       : string          — the field's accessible name, INDEPENDENT of
 *                                   the placeholder (Konzept N7.5). 🔴 Optional
 *                                   in G0: without it the mobile pill keeps
 *                                   `aria-label = placeholder` (the old form) and
 *                                   the desktop field keeps no explicit
 *                                   `aria-label` at all (its name falls back to
 *                                   the placeholder). G16 makes it required.
 *                                   🔴 House value for `label`: the placeholder
 *                                   WITHOUT the ellipsis (N7.5) — the ellipsis is NO label text. Where the two
 *                                   differ, the visible placeholder must still be
 *                                   CONTAINED in the accessible name — WCAG 2.5.3
 *                                   "Label in Name"; otherwise voice control
 *                                   ("click Search inbox") misses the field.
 *   onCommit    : (q) => void     — 🔴 turns on BUFFER MODE: the text is held
 *                                   locally and handed over after 300 ms of rest,
 *                                   Enter hands it over at once (N7.7). Without
 *                                   this prop the component behaves exactly as
 *                                   before — `onChange` fires on every keystroke
 *                                   and `value` is the input's value.
 *                                   🔴 Esc only clears in buffer mode: in the
 *                                   default path Esc did nothing before, and a
 *                                   clearing Esc there would be a behaviour
 *                                   change for the 16 existing callers.
 *   loading     : boolean         — shows a small progress circle at the END of
 *                                   the field while a server search runs (N7.12).
 *                                   Purely visual (`aria-hidden`): the loading
 *                                   announcement belongs to the list the caller
 *                                   dims (`aria-busy`, N7.12).
 *   shortcut    : string          — 🔴 opt-in single key (house value `'/'`) that
 *                                   puts the focus in this field, as long as the
 *                                   focus is not already inside a form control or
 *                                   inside an open dialog (N7.9). Without the prop
 *                                   no listener exists.
 *                                   🔴 SINGLE INSTANCE by design: the listener sits
 *                                   on `document`, so two search fields carrying
 *                                   `shortcut` on one page would BOTH take the
 *                                   focus. G0.10 (`ListToolbar`) has to ensure
 *                                   that only one list search sets it.
 *   focusRef    : React.Ref       — 🔴 optional (G0.10, UI2 Teil 10, rein additiv):
 *                                   zeigt nach jedem Rendern auf DASSELBE
 *                                   Eingabefeld, das `inputRef` hält — der
 *                                   Aufrufer kann den Fokus dorthin zurückgeben,
 *                                   ohne die Breitenform zu kennen (Desktop-
 *                                   `TextField` und Mobil-`InputBase` setzen
 *                                   beide `inputRef`). Ohne die Prop wird nichts
 *                                   zugewiesen; der Baum ist unverändert.
 *   disabled    : boolean         — 🔴 optional (G0.12, UI2 Teil 10, rein additiv,
 *                                   Vorgabe `false`): sperrt das Eingabefeld
 *                                   (`InputBase`/`TextField` `disabled`), damit
 *                                   „keine Eingabe ohne Folge bleibt" (N8.5). Im
 *                                   Puffermodus meldet der Baustein dann NICHTS —
 *                                   kein `onCommit`, kein Zeitgeber —, und das ✕
 *                                   ist mitgesperrt. Den GRUND nennt der Aufrufer
 *                                   sichtbar direkt über der Leiste; `DisabledReason`
 *                                   scheidet aus (es kann eine durchgereichte
 *                                   Ursache nicht benennen, `DisabledReason.jsx`
 *                                   `@dontUseFor`). Ohne die Prop ist der Baum
 *                                   unverändert.
 *   inputRole   : string          — 🔴 optional (G0.12, UI2 Teil 10, rein additiv,
 *                                   Vorgabe `undefined` = keine Rolle): die ROLLE
 *                                   des Eingabefelds, gesetzt als `inputProps.role`
 *                                   in BEIDEN `inputProps`-Literalen (Desktop-
 *                                   `TextField` und Mobil-`InputBase`). Hauswert
 *                                   der Aufrufer ist `'searchbox'` (Konzept N7.1/
 *                                   N7.5/P5/P34). 🔴 KEIN `type="search"`: der
 *                                   Grund oben (`placeholder`) bleibt — die Rolle
 *                                   kommt ueber `inputProps.role`, nicht ueber den
 *                                   Typ. Ohne die Prop bleibt das Feld `textbox`
 *                                   (heutiges Verhalten, byte-identisch fuer alle
 *                                   16 Bestandsaufrufer).
 *   sx          : object          — extra sx on wrapper (applied while open)
 *
 * 🔴 BUFFER MODE (`onCommit` given) — the exact contract:
 *   - the field shows the BUFFER, `value` only seeds it (on mount and whenever
 *     the caller changes it)
 *   - typing writes the buffer and schedules ONE commit 300 ms later
 *   - Enter commits immediately and cancels the pending timer
 *   - Esc clears on the FIRST press (and commits the empty text), the SECOND
 *     press with an empty field leaves the field (N7.9)
 *   - the ✕ clears and commits at once
 *   - `onChange` is NOT called — one writer per query, otherwise the URL would
 *     be written per keystroke and the debounce would be pointless (N7.7)
 *   - ONE COMMIT PER REST STATE: text that has already been handed over is not
 *     handed over again — Enter after the 300 ms rest does nothing, and neither
 *     does Enter on a text the caller set itself (deep link). Otherwise the
 *     caller would fire the same query twice.
 *   - a change of `value` from OUTSIDE discards a pending timer: "Clear all" (Z3)
 *     or removing the "Search: …" chip must not be undone 300 ms later by a
 *     timer that was still holding the deleted term.
 *   Everything above is inert without `onCommit`: the default path is
 *   behaviourally identical to the previous behaviour (G0-A).
 */
import React, { useState, useRef, useEffect } from 'react';
import { Box, CircularProgress, IconButton, InputBase, useTheme, useMediaQuery } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { FIELD_HEIGHT_SX } from '../../theme/tokens';

/** Ruhezeit, nach der der Puffer gemeldet wird (Konzept N7.7). */
const ENTPRELLUNG_MS = 300;

export default function SearchTrigger({
  value,
  onChange,
  placeholder = 'Search…',
  label,
  onCommit,
  loading = false,
  shortcut,
  focusRef,
  disabled = false,
  inputRole,
  sx = {},
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // 🔴 Puffer-Modus NUR mit `onCommit` (N7.7). Ohne ihn ist jede Zeile darunter
  // wirkungslos und der Baustein verhaelt sich wie vorher: `value` ist der
  // Feldwert, `onChange` feuert bei jedem Tastendruck.
  const mitPuffer = typeof onCommit === 'function';
  const [puffer, setPuffer] = useState(value == null ? '' : value);
  // Was im Feld STEHT — im Puffermodus der Puffer, sonst der Wert des Aufrufers.
  const angezeigt = mitPuffer ? puffer : value;
  const timerRef = useRef(null);
  // 🔴 Was zuletzt an `onCommit` ging — die „eine Meldung je Ruhe" (N7.7). Ohne
  // diesen Riegel meldete ein Enter NACH abgelaufener Ruhe denselben Text ein
  // zweites Mal, und der Aufrufer stiesse dieselbe Abfrage erneut an.
  const zuletztGemeldetRef = useRef(value == null ? '' : value);

  // Den laufenden Zeitgeber abraeumen. Steht VOR dem Nachfuehr-Effekt, weil der
  // ihn braucht: eine Wertaenderung von aussen verwirft den offenen Auftrag.
  const zeitgeberLoeschen = () => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Auto-open if there's already a query (e.g. URL param pre-fill)
  useEffect(() => {
    if (value) setOpen(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Der Puffer folgt einem WECHSEL von aussen (Deep-Link, „Clear all") — nicht
  // dem Tippen: waehrend des Tippens aendert der Aufrufer `value` nicht, der
  // Effekt laeuft also nicht und ueberschreibt nichts.
  // 🔴 Ein LAUFENDER Zeitgeber wird dabei VERWORFEN. Sonst meldet er 300 ms
  // spaeter den gerade von aussen geloeschten Begriff zurueck und schreibt ihn
  // in die URL („Clear all" in Z3, Chip „Search: …" entfernt, N7.10) — oder er
  // ueberschreibt einen per Deep-Link/Reiterwechsel gesetzten neuen Wert.
  // `zeitgeberLoeschen` steht bewusst NICHT in den Abhaengigkeiten: es liest nur
  // `timerRef` und ist bei jedem Render gleich. Als Dep liesse dieser Effekt bei
  // JEDEM Render laufen und loeschte den Entpreller, den `handleInput` gerade
  // gesetzt hat.
  useEffect(() => {
    if (!mitPuffer) return;
    zeitgeberLoeschen();
    const vonAussen = value == null ? '' : value;
    setPuffer(vonAussen);
    // Der Aufrufer kennt diesen Text bereits — ein Enter darauf meldet nichts
    // nach (dieselbe Regel wie bei der abgelaufenen Ruhe).
    zuletztGemeldetRef.current = vonAussen;
  }, [value, mitPuffer]);

  // Haengt beim Abbau kein Zeitgeber mehr, feuert `onCommit` in eine tote Flaeche.
  useEffect(() => () => {
    if (timerRef.current != null) clearTimeout(timerRef.current);
  }, []);

  // 🔴 Optionale Fokus-Senke (G0.10, rein additiv). `inputRef` traegt in BEIDEN
  // Breitenformen das Eingabefeld (Desktop-`TextField.inputRef`, Mobil-
  // `InputBase.inputRef`); die Senke zeigt nach jedem Rendern darauf. Ohne die
  // Prop ist der Effekt eine leere Zuweisung, der Baum unveraendert.
  useEffect(() => {
    if (focusRef) focusRef.current = inputRef.current;
  });

  // `/` setzt den Fokus hierher, solange er nicht schon in einem Bedienfeld
  // steht (N7.9) — sonst schriebe die Tastenkappe in ein fremdes Formular.
  // 🔴 Und solange kein Dialog offen ist: ein Blatt, eine Bestaetigung oder ein
  // `SlidePanel` haelt den Fokus in einer Falle. Ohne diesen zweiten Riegel
  // zoege `/` ihn HINTER den Dialog, waehrend dieser offen blieb — der Nutzer
  // tippte als Naechstes in ein Feld, das er nicht sieht.
  useEffect(() => {
    if (!shortcut) return undefined;
    const aufTaste = (e) => {
      if (e.key !== shortcut || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      const imBedienfeld = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA'
        || el.tagName === 'SELECT' || el.isContentEditable);
      if (imBedienfeld) return;
      if (el && el.closest && el.closest('[role="dialog"], [aria-modal="true"]')) return;
      e.preventDefault();
      setOpen(true);
      inputRef.current?.focus();
    };
    document.addEventListener('keydown', aufTaste);
    return () => document.removeEventListener('keydown', aufTaste);
  }, [shortcut]);

  // Ein Eingabevorgang: im Puffermodus in den Puffer plus spaeter Meldung, sonst
  // unveraendert durchreichen.
  const handleInput = (e) => {
    // 🔴 G0.12 (N8.5): gesperrt heisst „keine Eingabe ohne Folge". Ein `disabled`
    // am Eingabefeld unterbindet die Ereignisse schon im Browser; der Riegel
    // hier haelt auch den programmatischen Weg (Test, Auto-Fill) still.
    if (disabled) return;
    const naechster = e.target.value;
    if (!mitPuffer) {
      onChange?.(naechster);
      return;
    }
    setPuffer(naechster);
    zeitgeberLoeschen();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      zuletztGemeldetRef.current = naechster;
      onCommit(naechster);
    }, ENTPRELLUNG_MS);
  };

  // Enter meldet sofort, Esc leert beim ersten Druck und verlaesst das Feld beim
  // zweiten (N7.9). Beides nur im Puffermodus — im Vorgabepfad tat Esc nichts,
  // und ein leerendes Esc waere dort eine Verhaltensaenderung.
  const handleKeyDown = (e) => {
    if (!mitPuffer) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      zeitgeberLoeschen();
      // 🔴 „Eine Meldung je Ruhe": ist der Text schon gemeldet (Zeitgeber
      // abgelaufen, Deep-Link, ✕), meldet Enter nichts nach — sonst stiesse der
      // Aufrufer dieselbe Abfrage ein zweites Mal an.
      if (puffer === zuletztGemeldetRef.current) return;
      zuletztGemeldetRef.current = puffer;
      onCommit(puffer);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      if (puffer) {
        zeitgeberLoeschen();
        setPuffer('');
        zuletztGemeldetRef.current = '';
        onCommit('');
        return;
      }
      inputRef.current?.blur();
    }
  };

  const leeren = () => {
    // 🔴 G0.12 (N8.5): gesperrt leert nicht.
    if (disabled) return;
    if (mitPuffer) {
      zeitgeberLoeschen();
      setPuffer('');
      zuletztGemeldetRef.current = '';
      onCommit('');
      return;
    }
    onChange?.('');
  };

  // Der Lade-Kreis am Feldende (N7.12). `aria-hidden`, wie in `RefreshButton`:
  // er ist die SICHTBARE Haelfte, die angesagte ist das `aria-busy` der Liste.
  // 🔴 Er traegt `role="progressbar"` TROTZDEM — MUI setzt die Rolle unbedingt,
  // `aria-hidden` nimmt sie nur aus dem Baum der Hilfstechnik (die eigene Probe
  // S.5 findet den Kreis deshalb mit `hidden: true`). Getragen wird die
  // Entscheidung davon, dass die Sonde `PROGRESSBAR_PROBE` in
  // `frontend/tests/rma/helpers.ts:175` Balken INNERHALB von
  // `[aria-hidden="true"]` ausfiltert (`b.closest('[aria-hidden="true"]')`).
  // Ohne diesen Filter haengt ein unbestimmter Balken ohne `aria-valuenow` das
  // `waitForPageLoad` JEDES RMA-Specs auf. 🔴 `aria-hidden` hier NICHT
  // entfernen, auch nicht „weil die Rolle ja ohnehin da ist".
  const ladekring = <CircularProgress size={16} color="inherit" aria-hidden="true" />;

  // iOS Safari only opens the soft keyboard when focus() runs SYNCHRONOUSLY
  // inside the user-gesture handler on an input that is already mounted in
  // the DOM. The previous implementation rendered the input only after the
  // expand transition (setTimeout 120ms) which lost gesture context — costing
  // a second tap. Fix: the input is always mounted AND always visible, so the
  // tap focuses it natively; the container handler below is the second path
  // for taps that land on the icon or the pill's padding.
  const expandAndFocus = () => {
    setOpen(true);
    inputRef.current?.focus();
  };

  // Collapse on blur — but only when focus truly leaves this component
  const handleBlur = (e) => {
    if (containerRef.current?.contains(e.relatedTarget)) return;
    setOpen(false);
    // Don't clear the query — the pill turns primary to signal an active filter
  };

  // ── Desktop: plain TextField (unchanged UX) ──────────────────
  if (!isMobile) {
    return (
      <TextField
        size="small"
        placeholder={placeholder}
        value={angezeigt}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        // 🔴 G0.12 (N8.5): die Sperre sitzt am Eingabefeld — ohne die Prop ist
        // `disabled` falsch und React laesst das Attribut aus (additiv).
        disabled={disabled}
        // 🔴 Der Verweis auf das Eingabefeld gilt ab G0.3 auch hier: `shortcut`
        // setzt den Fokus ueber `inputRef`, und das Esc-Blur im Puffermodus
        // ebenso. Bis dahin trug nur der Telefon-Zweig ihn — ein `/` waere am
        // Desktop wirkungslos geblieben, ohne dass etwas darauf hindeutet.
        inputRef={inputRef}
        // Ohne `label` bleibt das Attribut weg — die zugaengliche Name kommt
        // dann wie bisher aus dem Platzhalter. Mit `label` steht sie fest.
        // `aria-keyshortcuts` ebenso nur mit gesetztem `shortcut`: ohne die Prop
        // ist der Wert `undefined` und React laesst das Attribut aus, die 16
        // Bestandsaufrufe rendern also unveraendert.
        inputProps={{
          'aria-label': label,
          'aria-keyshortcuts': shortcut || undefined,
          // 🔴 G0.12 (P5, rein additiv): die ROLLE des Eingabefelds, wenn der
          // Aufrufer sie setzt. Ohne die Prop ist der Wert `undefined` und React
          // laesst das Attribut aus — die 16 Bestandsaufrufe bleiben `textbox`.
          // 🔴 Das DESKTOP-Literal traegt sie MIT: `SearchTrigger` verzweigt auf
          // `useMediaQuery(down('sm'))`, und die `listen`-Probe faehrt einen
          // 1440-px-Desktop-Viewport — sie rendert genau dieses `TextField`.
          role: inputRole,
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: loading ? (
            <InputAdornment position="end">{ladekring}</InputAdornment>
          ) : undefined,
        }}
        sx={{ minWidth: 220, ...sx }}
      />
    );
  }

  // ── Mobile: pill with a VISIBLE placeholder, grows to the row width on use.
  //
  // 🔴 Der Platzhalter ist der einzige sichtbare Name dieses Feldes. Er steht
  // deshalb im GESCHLOSSENEN Zustand auf dem Schirm — bis 2026-10-07 war das
  // Feld auf `width: 0` gezogen und nur eine Lupe zu sehen (Fotobefund
  // „rma (m): Suchfeld leere Pille nur mit Lupe", §F5 im Plan). Das Eingabefeld bleibt dauerhaft gemountet: nur so
  // oeffnet ein Tipp die iOS-Tastatur im selben Gestus.
  //
  // Die Breite: geschlossen `1 1 auto` (der intrinsische Platz von Symbol +
  // Platzhalter, schrumpfbar), offen `1` plus das `sx` des Aufrufers. Die
  // `flex`/`width`-Schluessel aus `sx` werden geschlossen NICHT angewandt —
  // sonst zoege ein Aufrufer mit `sx={{ flex: 1 }}` die Pille schon im
  // Ruhezustand auf die ganze Zeile und verdraengte die Knoepfe daneben.
  const hasQuery = Boolean(angezeigt);
  const { flex: _f, width: _w, minWidth: _mw, maxWidth: _xw, ...safeSx } = sx;

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flex: open ? 1 : '1 1 auto',
        minWidth: 0,
        ...(open ? sx : safeSx),
      }}
    >
      <Box
        onClick={expandAndFocus}
        sx={{
          flex: 1,
          minWidth: 0,
          bgcolor: 'var(--glass-bg)',
          backdropFilter: 'saturate(160%) blur(10px)',
          WebkitBackdropFilter: 'saturate(160%) blur(10px)',
          border: '1px solid',
          borderColor: hasQuery ? 'primary.main' : 'var(--glass-border)',
          boxShadow: 'var(--glass-shadow)',
          borderRadius: 99,
          // 🔴 T3-5 (UI-Durchgang 2, Teil 3): der Boden dieser Pille kommt aus
          // dem Token, nicht aus einer eigenen Zahl. Sie stand hier als `height:
          // 40` — fest, auf jeder Breite —, waehrend `FilterSheet.jsx` (der „Filters"-Knopf)
          // daneben schon den Theme-Boden trug: in EINER Werkzeugzeile standen
          // 40 px und 44 px. `FIELD_HEIGHT_SX` ist die Hausform
          // `{ xs: 44, sm: 40 }` plus Zeigerabfrage; dieser Zweig rendert nur
          // unter `sm` (`isMobile`), es greift also die 44 — die `sm`-Haelfte
          // (40) und die Zeigerabfrage werden hier NIE wirksam. Ab `sm` rendert
          // der Desktop-Zweig oben ein `TextField size="small"`, ohne dieses Token.
          ...FIELD_HEIGHT_SX,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 0.75,
          px: 1.5,
          cursor: 'text',
          color: hasQuery ? 'primary.main' : 'text.secondary',
          transition: 'padding 160ms ease, gap 160ms ease',
        }}
      >
        <SearchIcon sx={{
          fontSize: open ? 16 : 18,
          color: open ? 'text.disabled' : 'inherit',
          flexShrink: 0,
        }} />
        <InputBase
          inputRef={inputRef}
          value={angezeigt}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          // 🔴 G0.12 (N8.5): dieselbe Sperre an der Telefon-Pille. Ohne die Prop
          // ist `disabled` falsch und der Baum unveraendert (additiv).
          disabled={disabled}
          // Ein Tastaturfokus oeffnet das Feld wie ein Tipp — vorher war das
          // Eingabefeld im Ruhezustand `tabIndex: -1` und damit fuer die
          // Tastatur unerreichbar.
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          inputProps={{
            inputMode: 'search',
            enterKeyHint: 'search',
            autoComplete: 'off',
            // 🔴 Ohne `label` steht hier der PLATZHALTER — die Vorgabe aus G0
            // (G0-A). Mit `label` ist der Name vom Platzhalter unabhaengig
            // (N7.5), und der Platzhalter darf sich aendern, ohne den Namen
            // mitzunehmen.
            'aria-label': label || placeholder,
            // 🔴 Nur mit gesetztem `shortcut` — sonst `undefined` und damit kein
            // Attribut im DOM (Additivitaet fuer die 16 Bestandsaufrufe).
            'aria-keyshortcuts': shortcut || undefined,
            // 🔴 G0.12 (P5): dieselbe Rolle am MOBILEN `InputBase` — beide
            // Literale tragen sie (Wiedervorlage W-A), sonst fände die Rolle nur
            // eine der zwei Breitenformen.
            role: inputRole,
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            // 16px prevents iOS Safari from zooming on input focus
            fontSize: 16,
            '& input': { p: 0, textOverflow: 'ellipsis' },
            // 🔴 `text.secondary`, NICHT `text.disabled` (F5, 2026-10-07):
            // seit der Platzhalter im Ruhezustand sichtbar ist, ist er der
            // EINZIGE Name des Feldes. `text.disabled` steht bei ~2,7 : 1 und
            // ist damit unter AA für Text, den man lesen soll — dieselbe
            // Begründung wie bei `EmptyState`/`SeitenleisteBearbeiten`.
            '& input::placeholder': { color: 'text.secondary', opacity: 1 },
          }}
        />
        {/* Der Lade-Kreis sitzt am FELDENDE, vor dem Loeschkreuz (N7.12) — die
            Lupe bleibt, wo sie ist: sie sagt WAS durchsucht wird, der Kreis
            sagt DASS gerade gesucht wird. */}
        {loading && ladekring}
        {angezeigt && (
          // 🔴 Ein ECHTER `IconButton`, kein `<Box role="button">` (F5-Nachzug,
          // Regel-16-B1 + R15-W5, 2026-10-08). Die alte Fassung sagte
          // „hier ist ein Knopf" an, war aber ein `<div>` ohne `tabIndex` und
          // ohne `onKeyDown`: nicht in der Tab-Reihenfolge, Enter und Leertaste
          // taten nichts (WCAG 2.1.1, Stufe A). Aus „stumm" war „irrefuehrend"
          // geworden, als der Name dazukam. `IconButton` ist ein `<button>` und
          // bringt Tab-Stopp und Tastenaktivierung mit; auf grobem Zeiger traegt
          // er den 44-px-Boden aus `theme.js` (`MuiIconButton.styleOverrides.root`
          // → `minWidth/minHeight: 44` unter `sm`).
          <IconButton
            size="small"
            aria-label="Clear search"
            // 🔴 G0.12 (N8.5): gesperrt leert auch das ✕ nicht — sonst bliebe ein
            // Weg in die Suche offen, den die Sperre schliessen soll.
            disabled={disabled}
            // Fokus im Feld halten: `preventDefault` auf `mousedown` nimmt dem
            // Knopf den MAUSfokus, damit das Eingabefeld nicht verliert, was es
            // hat. Der Tastaturweg bleibt unberuehrt — `preventDefault` gilt nur
            // fuer den Zeiger, `Tab` und `Enter` erreichen den Knopf weiterhin.
            onMouseDown={(e) => e.preventDefault()}
            // Nicht hochblubbern lassen: der umgebende Kasten oeffnet das Feld
            // per `onClick` (`expandAndFocus`) — Loeschen darf es nicht ueber
            // diesen Weg oeffnen.
            //
            // 🔴 Fokus zurueck ins Feld, aber NUR auf dem TASTATURWEG
            // (R16-W-B + R15-W1, 2026-10-08). Der Knopf haengt an `{value && …}`:
            // mit dem Leeren wird er ABGEHAENGT. Auf der Tastatur liegt der
            // Fokus auf dem Knopf, der Knopf verschwindet, und der Browser setzt
            // ihn auf `document.body`; der naechste `Tab` beginnt dann am
            // Dokumentanfang statt am Feld. `focus()` laeuft SYNCHRON im
            // Handler, also bevor React neu rendert und den Knopf abhaengt — das
            // Eingabefeld ist dauerhaft gemountet und nimmt den Fokus sofort an,
            // ohne Zwischenzustand mit dem Fokus auf `document.body`.
            //
            // 🔴 Auf dem ZEIGERWEG bleibt der Fokus, wo er ist. Unbedingt
            // gerufen klappte `focus()` die Pille im EINGEKLAPPTEN Zustand auf
            // (`onFocus` des Eingabefelds → `setOpen(true)`) — ein Tipp auf das
            // × oeffnete also das Feld, das der geschlossene Zustand gerade
            // nicht zeigen soll. Den Zeigerweg erkennt der Knopf an sich selbst:
            // er traegt den Fokus dann nicht, weil `onMouseDown`+`preventDefault`
            // ihm den Mausfokus nimmt. Im offenen Zustand liegt der Fokus
            // ohnehin im Feld, dort aendert der Verzicht nichts.
            onClick={(e) => {
              // VOR `leeren()`: der Aufruf stoesst einen Eltern-Render an,
              // der den Knopf abhaengen kann.
              const perTastatur = document.activeElement === e.currentTarget;
              e.stopPropagation();
              // Im Puffermodus leert und meldet `leeren()` in einem Zug (die
              // 300 ms Ruhe gelten fuer das TIPPEN, nicht fuer einen Klick).
              leeren();
              if (perTastatur) inputRef.current?.focus();
            }}
            // `text.secondary` statt `text.disabled` (R16-W5): die Ikone ist ein
            // Bedienelement und steht bei `text.disabled` unter der 3:1-Grenze
            // aus WCAG 1.4.11 — dieselbe Zahl, dieselbe Farbe, dieselbe
            // Begruendung wie beim Platzhalter drei Zeilen weiter oben.
            sx={{ color: 'text.secondary', flexShrink: 0 }}
          >
            <CloseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
